import React, { useEffect, useState } from 'react';
import { apiRequest } from '../../../service/apiRequest';
import {
  Table,
  Button,
  Flex,
  Breadcrumb,
  Form,
  message,
  Popconfirm,
  Space,
  Pagination,
  Switch,
  TableProps,
} from 'antd';
import { createStyles } from 'antd-style';
import { HomeOutlined, EditOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import ClientModal from './ClientModel';
import { showSuccess, showError } from '../../../components/errorHandler';
import type { PopconfirmProps, TableColumnsType } from 'antd';
import { useCurrentModule } from '../../../hooks/useCurrentModule';
import { useSelector } from 'react-redux';
const useStyle = createStyles(({ css }) => ({
  customTable: css`
    .ant-table {
      &-container {
        .ant-table-body,
        .ant-table-content {
          scrollbar-width: thin;
          scrollbar-color: #eaeaea transparent;
        }
      }
    }
  `,
}));

interface Clients {
  client_id: number;
  client_name: string;
  client_mobile: number;
  client_status: number;
  client_organization_id?: string | null;
}

const Client: React.FC = () => {
  const client = useSelector((state: any) => state.client?.client);
  const [clients, setClients] = useState<Clients[]>([]);
  const [form] = Form.useForm();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [initialValues, setInitialValues] = useState<Clients | null>(null);
  const { styles } = useStyle();
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const currentModule = useCurrentModule();

  const [filteredInfo, setFilteredInfo] = useState<Record<string, any>>({});
  const [sortedInfo, setSortedInfo] = useState<any>({});

  console.log('Current Module:', clients);
  const cancel: PopconfirmProps['onCancel'] = (e) => {
    console.log(e);
    showError('Click on No');
  };

  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  const fetchClient = async () => {
    try {
      const data = await apiRequest<Clients[]>({
        method: 'get',
        url: '/getall-all-client',
      });
      setClients(data);
    } catch (error) {
      showError('Failed to fetch clients');
      console.error(error);
    }
  };

  useEffect(() => {
    fetchClient();
  }, []);

  const handleEditClick = (record: Clients) => {
    setIsEditMode(true);
    setInitialValues({
      client_id: record.client_id,
      client_name: record.client_name,
      client_mobile: record.client_mobile,
      client_status: record.client_status,
      ...(client?.client_role === 'super-master' && {
        client_organization_id: record.client_organization_id,
      }),
    });
    setIsModalVisible(true);
  };

  const handleCreate = () => {
    fetchClient();
  };

  const showCreateModal = () => {
    setIsEditMode(false);
    setInitialValues(null);
    setIsModalVisible(true);
  };

  const hideCreateModal = () => {
    setIsModalVisible(false);
    setIsEditMode(false);
    setInitialValues(null);
  };


  const handleStatusToggle = async (subjectId: number) => {
    setClients((prev) =>
      prev.map((item) =>
        item.client_id === subjectId
          ? { ...item, client_status: item.client_status === 1 ? 0 : 1 }
          : item
      )
    );

    try {
      await apiRequest({
        method: 'put',
        url: `/update-status-client/${subjectId}`,
      });
      showSuccess('Client status updated');
    } catch (error) {
      showError('Failed to update status');
      setClients((prev) =>
        prev.map((item) =>
          item.client_id === subjectId
            ? { ...item, client_status: item.client_status === 1 ? 0 : 1 }
            : item
        )
      );
    }
  };

  const handleTableChange: TableProps<Clients>['onChange'] = (pagination, filters, sorter) => {
    setFilteredInfo(filters);
    setSortedInfo(sorter);
  };




  const columns: TableColumnsType<Clients> = [
    {
      title: 'Client Name',
      key: 'client_name',
      dataIndex: 'client_name',
      width: 400,
      filters: Array.from(new Set(clients.map((u) => u.client_name))).map((name) => ({
        text: name,
        value: name,
      })),
      filterSearch: true,
      filterMode: 'tree',
      filteredValue: filteredInfo.client_name || null,
      onFilter: (value, record) => record.client_name.includes(value as string),
      sorter: (a, b) => a.client_name.localeCompare(b.client_name),
      sortOrder: sortedInfo.columnKey === 'client_name' ? sortedInfo.order : null,
      sortDirections: ['ascend', 'descend'],
      ellipsis: true,
    },
    {
      title: 'Mobile Number',
      key: 'client_mobile',
      dataIndex: 'client_mobile',
      width: 400,
      filters: Array.from(new Set(clients.map((s) => s.client_mobile))).map((mobile) => ({
        text: mobile,
        value: mobile,
      })),
      filterSearch: true,
      filterMode: 'tree',
      filteredValue: filteredInfo.client_mobile || null,
      // onFilter: (value, record) => record.client_mobile.includes(value as string),
      // sorter: (a, b) => a.client_mobile.localeCompare(b.client_mobile),
      sortOrder: sortedInfo.columnKey === 'client_mobile' ? sortedInfo.order : null,
      sortDirections: ['ascend', 'descend'],
      ellipsis: true,
    },
  ];


  // Conditionally add action column
  if (currentModule?.right_edit === 'Y' || currentModule?.right_delete === 'Y') {
    columns.push(
      {
        title: 'Action',
        width: 150,
        align: 'center', 
        render: (_: any, record: Clients) => (
          <Space size="middle">
            {currentModule?.right_edit === 'Y' && (
              <>
                {/* <Switch
                  checkedChildren="Active"
                  unCheckedChildren="Inactive"
                  checked={!!record.client_status}
                  onChange={() => handleStatusToggle(record.client_id)}
                /> */}
                <Button onClick={() => handleEditClick(record)} className="px-8">
                  <EditOutlined />
                </Button>
              </>
            )}

          </Space>
        ),
      });
  }


  return (
    <>
      <Space
        style={{
          display: 'flex',
          marginBottom: 16,
          alignItems: 'center',
          width: '100%',
          justifyContent: 'space-between',
        }}
      >
        <Breadcrumb style={{ display: 'inline-block', margin: 0 }}>
          <Breadcrumb.Item href="/">
            <HomeOutlined />
          </Breadcrumb.Item>
          <Breadcrumb.Item>View Clients</Breadcrumb.Item>
        </Breadcrumb>

        {currentModule?.right_add === 'Y' && (
          <Flex gap="small">
            <Button type="primary" onClick={showCreateModal}>
              Add Client
            </Button>
          </Flex>
        )}
      </Space>


      <div
        style={{
          minHeight: 360,
          background: colorBgContainer,
          borderRadius: borderRadiusLG,
        }}
      >
        <Table<Clients>
          tableLayout="auto"
          className={`${styles.customTable} scrollable-table`}
          columns={columns}
          onChange={handleTableChange}
          rowKey="client_id"
          dataSource={clients.slice(
            (pagination.current - 1) * pagination.pageSize,
            pagination.current * pagination.pageSize
          )}
          bordered
          pagination={false}
          scroll={{ y: 37 * 15 }}
          locale={{ emptyText: 'No client found' }}
          footer={() => (
            <Flex justify="space-between">
              <span>
                Showing {(pagination.current - 1) * pagination.pageSize + 1}–
                {Math.min(pagination.current * pagination.pageSize, clients.length)} of{' '}
                {clients.length}
              </span>
              <Pagination
                {...pagination}
                total={clients.length}
                showSizeChanger
                onChange={(page, pageSize) => setPagination({ current: page, pageSize })}
              />
            </Flex>
          )}
        />
      </div>

      <ClientModal
        visible={isModalVisible}
        onCancel={hideCreateModal}
        onCreate={handleCreate}
        isEditMode={isEditMode}
        initialValues={initialValues}
      />
    </>
  );
};

export default Client;